package top.wcpe.allincore.ui.web.preview

import kotlinx.browser.document
import top.wcpe.allincore.ui.web.asset.AssetFileSystemDirectoryHandle
import top.wcpe.allincore.ui.web.asset.AssetFileSystemFileHandle
import top.wcpe.allincore.ui.web.asset.AssetPathPolicy
import top.wcpe.allincore.ui.web.asset.FileSystemAssetSourceAdapter
import top.wcpe.allincore.ui.web.asset.browserFileSystemDirectoryHandle
import top.wcpe.allincore.ui.web.asset.browserFileSystemFileHandle
import kotlin.js.Promise

internal data class DirFile(val name: String, val relPath: String)

internal class DirectorySelection internal constructor(
    rootHandle: AssetFileSystemDirectoryHandle,
    val files: List<DirFile>,
    handles: Map<String, AssetFileSystemFileHandle>,
) {
    private var rootHandle: AssetFileSystemDirectoryHandle? = rootHandle
    internal val assetHandles = handles.toMutableMap()
    private var disposed = false

    internal fun createAssetSource(): FileSystemAssetSourceAdapter {
        val root = rootHandle ?: error("目录选择已释放")
        return FileSystemAssetSourceAdapter(root, assetHandles.toMap(), pathAllowed = DirectoryPicker::allowsAssetPath)
    }

    fun dispose() {
        if (disposed) return
        disposed = true
        rootHandle = null
        assetHandles.clear()
    }
}

internal sealed interface PickerEntry {
    val name: String
}

internal data class PickerFileEntry(
    override val name: String,
    val assetHandle: AssetFileSystemFileHandle,
) : PickerEntry

internal data class PickerDirectoryEntry(
    override val name: String,
    val directory: PickerDirectory,
) : PickerEntry

internal interface PickerDirectory {
    val assetHandle: AssetFileSystemDirectoryHandle
    fun entries(): Promise<List<PickerEntry>>
}

internal object DirectoryPicker {
    fun pick(): Promise<DirectorySelection?> {
        val picker = js("globalThis.showDirectoryPicker")
        if (picker == null) return Promise.resolve(null)
        val opening = picker().unsafeCast<Promise<dynamic>>()
        return opening.then<dynamic>(
            onFulfilled = { handle: dynamic -> enumerate(BrowserPickerDirectory(handle)) },
            onRejected = { _: Throwable -> Promise.resolve(null) },
        ).unsafeCast<Promise<DirectorySelection?>>()
    }

    internal fun enumerate(root: PickerDirectory): Promise<DirectorySelection?> {
        val handles = linkedMapOf<String, AssetFileSystemFileHandle>()
        return collect(root, "", handles).then {
            val files = handles.keys.filter(::isAllin).sorted().map(::dirFile)
            if (files.isEmpty()) null else DirectorySelection(root.assetHandle, files, handles)
        }
    }

    private fun collect(
        directory: PickerDirectory,
        prefix: String,
        handles: MutableMap<String, AssetFileSystemFileHandle>,
    ): Promise<Unit> = directory.entries().then { entries ->
        collectEntries(entries.sortedBy(PickerEntry::name), prefix, handles, 0)
    }.unsafeCast<Promise<Unit>>()

    private fun collectEntries(
        entries: List<PickerEntry>,
        prefix: String,
        handles: MutableMap<String, AssetFileSystemFileHandle>,
        index: Int,
    ): Promise<Unit> {
        if (index >= entries.size) return Promise.resolve(Unit)
        val entry = entries[index]
        return collectEntry(entry, prefix, handles).then {
            collectEntries(entries, prefix, handles, index + 1)
        }.unsafeCast<Promise<Unit>>()
    }

    private fun collectEntry(
        entry: PickerEntry,
        prefix: String,
        handles: MutableMap<String, AssetFileSystemFileHandle>,
    ): Promise<Unit> {
        val path = allowedPath(prefix, entry.name) ?: return Promise.resolve(Unit)
        return when (entry) {
            is PickerFileEntry -> {
                if (supportedAsset(path)) handles[path] = entry.assetHandle
                Promise.resolve(Unit)
            }
            is PickerDirectoryEntry -> collect(entry.directory, path, handles)
        }
    }

    private fun allowedPath(prefix: String, name: String): String? {
        if (name.startsWith('.')) return null
        val raw = if (prefix.isEmpty()) name else "$prefix/$name"
        return runCatching { AssetPathPolicy.external(raw) }.getOrNull()
    }

    internal fun allowsAssetPath(path: String): Boolean {
        if (path.split('/').any { it.startsWith('.') }) return false
        val canonical = runCatching { AssetPathPolicy.external(path) }.getOrNull() ?: return false
        return canonical == path && supportedAsset(path)
    }

    private fun supportedAsset(path: String): Boolean {
        val lower = path.lowercase()
        return ALLOWED_SUFFIXES.any(lower::endsWith)
    }

    private fun isAllin(path: String): Boolean = path.lowercase().endsWith(ALLIN_SUFFIX)

    private fun dirFile(path: String): DirFile = DirFile(path.substringAfterLast('/'), path)

    private val ALLOWED_SUFFIXES = listOf(
        ".allin",
        ".mock.json",
        ".json",
        ".bbmodel",
        ".png",
        ".jpg",
        ".jpeg",
        ".webp",
    )
    private const val ALLIN_SUFFIX = ".allin"
}

private class BrowserPickerDirectory(private val handle: dynamic) : PickerDirectory {
    override val assetHandle: AssetFileSystemDirectoryHandle = browserFileSystemDirectoryHandle(handle)

    override fun entries(): Promise<List<PickerEntry>> {
        val output = mutableListOf<PickerEntry>()
        return consumeIterator(handle.entries(), output)
    }

    private fun consumeIterator(iterator: dynamic, output: MutableList<PickerEntry>): Promise<List<PickerEntry>> {
        val next = iterator.next().unsafeCast<Promise<dynamic>>()
        return next.then<dynamic> { result: dynamic ->
            if (result.done as Boolean) return@then output
            val pair: dynamic = result.value
            val name = pair[0] as String
            val child: dynamic = pair[1]
            appendEntry(name, child, output)
            consumeIterator(iterator, output)
        }.unsafeCast<Promise<List<PickerEntry>>>()
    }

    private fun appendEntry(name: String, child: dynamic, output: MutableList<PickerEntry>) {
        when (child.kind as String) {
            "file" -> output += PickerFileEntry(name, browserFileSystemFileHandle(child))
            "directory" -> output += PickerDirectoryEntry(name, BrowserPickerDirectory(child))
        }
    }
}

internal object RootAllinSelector {
    fun select(files: List<DirFile>): DirFile? {
        if (files.isEmpty()) return null
        val root = files.filter { '/' !in it.relPath }
        return (root.ifEmpty { files }).minByOrNull { it.relPath }
    }
}

internal fun installOpenDirectoryButton(app: PreviewApp) {
    if (!(js("typeof globalThis.showDirectoryPicker === 'function'") as Boolean)) {
        showDirectoryUnsupportedHint()
        return
    }
    if (document.getElementById(OPEN_DIRECTORY_BUTTON_ID) != null) return
    // 有 API 时移除可能残留的不支持提示，避免重复干扰
    document.getElementById(DIRECTORY_UNSUPPORTED_ID)?.asDynamic()?.remove()
    val button = document.createElement("button")
    button.id = OPEN_DIRECTORY_BUTTON_ID
    button.textContent = "打开目录"
    styleOpenDirectoryButton(button)
    button.addEventListener("click", {
        DirectoryPicker.pick().then { selection ->
            selection?.let(app::loadDirectory)
            Unit
        }.catch { Unit }
    })
    document.body?.appendChild(button)
}

/** 无 showDirectoryPicker 时给出固定中文替代路径提示，禁止静默 return。 */
private fun showDirectoryUnsupportedHint() {
    if (document.getElementById(DIRECTORY_UNSUPPORTED_ID) != null) return
    if (document.getElementById(OPEN_DIRECTORY_BUTTON_ID) != null) return
    val hint = document.createElement("div")
    hint.id = DIRECTORY_UNSUPPORTED_ID
    hint.setAttribute("role", "status")
    hint.textContent = DIRECTORY_UNSUPPORTED_MESSAGE
    styleDirectoryUnsupportedHint(hint)
    document.body?.appendChild(hint)
}

private fun styleOpenDirectoryButton(button: org.w3c.dom.Element) {
    val style = button.asDynamic().style
    style.position = "fixed"
    style.top = "8px"
    style.left = "8px"
    style.zIndex = "9999"
    style.padding = "6px 10px"
    style.cursor = "pointer"
}

private fun styleDirectoryUnsupportedHint(element: org.w3c.dom.Element) {
    val style = element.asDynamic().style
    style.position = "fixed"
    style.top = "8px"
    style.left = "8px"
    style.zIndex = "9999"
    style.padding = "6px 10px"
    style.maxWidth = "360px"
    style.background = "rgba(40,42,54,.94)"
    style.color = "#FFD9A8"
    style.border = "1px solid #6B7085"
    style.borderRadius = "4px"
    style.font = "12px monospace"
}

private const val OPEN_DIRECTORY_BUTTON_ID = "preview-open-directory"
internal const val DIRECTORY_UNSUPPORTED_ID = "preview-directory-unsupported"
internal const val DIRECTORY_UNSUPPORTED_MESSAGE =
    "当前浏览器不支持目录选择，请改用 ?src= URL 或 Pages 部署"
